import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import StatusBanner from '../ui/StatusBanner';
import './PlatformAttendanceSettingsPanel.css';

const DEFAULT_FORM = {
  lateThresholdMinutes: '5',
  absentThresholdMinutes: '30',
  calloutNoticeRequiredMinutes: '60',
  noCallNoShowThresholdMinutes: '120',
  escalateAfterOccurrences: '3',
  supervisorNotificationEnabled: true,
  directorEscalationEnabled: true
};

function PlatformAttendanceSettingsPanel({ companyId, companyName }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applySettings = useCallback((settings = {}) => {
    setForm({
      lateThresholdMinutes: String(settings.lateThresholdMinutes ?? DEFAULT_FORM.lateThresholdMinutes),
      absentThresholdMinutes: String(settings.absentThresholdMinutes ?? DEFAULT_FORM.absentThresholdMinutes),
      calloutNoticeRequiredMinutes: String(settings.calloutNoticeRequiredMinutes ?? DEFAULT_FORM.calloutNoticeRequiredMinutes),
      noCallNoShowThresholdMinutes: String(settings.noCallNoShowThresholdMinutes ?? DEFAULT_FORM.noCallNoShowThresholdMinutes),
      escalateAfterOccurrences: String(settings.escalateAfterOccurrences ?? DEFAULT_FORM.escalateAfterOccurrences),
      supervisorNotificationEnabled: settings.supervisorNotificationEnabled !== false,
      directorEscalationEnabled: settings.directorEscalationEnabled !== false
    });
  }, []);

  const loadSettings = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const payload = await apiRequest(`/api/platform/companies/${companyId}/settings/attendance`);
      applySettings(payload?.settings || {});
    } catch (err) {
      setError(err?.message || 'Unable to load attendance rules.');
    } finally {
      setIsLoading(false);
    }
  }, [applySettings, companyId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSave = useCallback(async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        lateThresholdMinutes: Number(form.lateThresholdMinutes),
        absentThresholdMinutes: Number(form.absentThresholdMinutes),
        calloutNoticeRequiredMinutes: Number(form.calloutNoticeRequiredMinutes),
        noCallNoShowThresholdMinutes: Number(form.noCallNoShowThresholdMinutes),
        escalateAfterOccurrences: Number(form.escalateAfterOccurrences),
        supervisorNotificationEnabled: form.supervisorNotificationEnabled === true,
        directorEscalationEnabled: form.directorEscalationEnabled === true
      };
      const data = await apiRequest(`/api/platform/companies/${companyId}/settings/attendance`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      applySettings(data?.settings || payload);
      setSuccess('Attendance rules saved.');
    } catch (err) {
      setError(err?.message || 'Unable to save attendance rules.');
    } finally {
      setIsSaving(false);
    }
  }, [applySettings, companyId, form]);

  return (
    <section
      className="director-card platform-attendance-settings-panel"
      data-testid="platform-attendance-settings-panel"
    >
      <div className="role-section-header">
        <h2>Attendance Rules</h2>
        <span>Company Settings</span>
      </div>
      <p className="platform-attendance-settings-copy">
        Configure company-level attendance thresholds and escalation defaults for workforce operations.
      </p>

      {error ? <StatusBanner kind="error" message={error} live /> : null}
      {success ? <StatusBanner kind="success" message={success} live /> : null}

      {isLoading ? (
        <div className="platform-attendance-settings-loading">Loading attendance rules…</div>
      ) : (
        <form className="platform-attendance-settings-form" onSubmit={handleSave}>
          <div className="platform-attendance-settings-grid">
            <FormField label="Late threshold minutes" htmlFor="attendance-late-threshold" hint="Minutes after shift start before a late event is recognized.">
              <input
                id="attendance-late-threshold"
                type="number"
                min="0"
                value={form.lateThresholdMinutes}
                onChange={(event) => updateField('lateThresholdMinutes', event.target.value)}
                data-testid="attendance-settings-late-threshold"
              />
            </FormField>

            <FormField label="Absent threshold minutes" htmlFor="attendance-absent-threshold" hint="Minutes after shift start before the absence threshold applies.">
              <input
                id="attendance-absent-threshold"
                type="number"
                min="0"
                value={form.absentThresholdMinutes}
                onChange={(event) => updateField('absentThresholdMinutes', event.target.value)}
                data-testid="attendance-settings-absent-threshold"
              />
            </FormField>

            <FormField label="Callout notice required minutes" htmlFor="attendance-callout-notice" hint="Minimum notice expected before a scheduled shift.">
              <input
                id="attendance-callout-notice"
                type="number"
                min="0"
                value={form.calloutNoticeRequiredMinutes}
                onChange={(event) => updateField('calloutNoticeRequiredMinutes', event.target.value)}
                data-testid="attendance-settings-callout-notice"
              />
            </FormField>

            <FormField label="No-call/no-show threshold minutes" htmlFor="attendance-ncns-threshold" hint="Minutes after shift start before no-call/no-show escalation applies.">
              <input
                id="attendance-ncns-threshold"
                type="number"
                min="0"
                value={form.noCallNoShowThresholdMinutes}
                onChange={(event) => updateField('noCallNoShowThresholdMinutes', event.target.value)}
                data-testid="attendance-settings-ncns-threshold"
              />
            </FormField>

            <FormField label="Escalate after occurrences" htmlFor="attendance-escalate-after" hint="Escalate once an employee reaches this many tracked occurrences.">
              <input
                id="attendance-escalate-after"
                type="number"
                min="1"
                value={form.escalateAfterOccurrences}
                onChange={(event) => updateField('escalateAfterOccurrences', event.target.value)}
                data-testid="attendance-settings-escalate-after"
              />
            </FormField>
          </div>

          <div className="platform-attendance-settings-toggles">
            <label className="platform-attendance-settings-toggle">
              <input
                type="checkbox"
                checked={form.supervisorNotificationEnabled}
                onChange={(event) => updateField('supervisorNotificationEnabled', event.target.checked)}
                data-testid="attendance-settings-supervisor-notification"
              />
              <span>Supervisor notification enabled</span>
            </label>
            <label className="platform-attendance-settings-toggle">
              <input
                type="checkbox"
                checked={form.directorEscalationEnabled}
                onChange={(event) => updateField('directorEscalationEnabled', event.target.checked)}
                data-testid="attendance-settings-director-escalation"
              />
              <span>Director escalation enabled</span>
            </label>
          </div>

          <div className="platform-attendance-settings-actions">
            <Button
              type="submit"
              size="sm"
              loading={isSaving}
              loadingLabel="Saving…"
              data-testid="attendance-settings-save"
            >
              Save attendance rules
            </Button>
          </div>

          {companyName ? (
            <p className="platform-attendance-settings-company">{companyName}</p>
          ) : null}
        </form>
      )}
    </section>
  );
}

export default PlatformAttendanceSettingsPanel;
